import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { patients } from '@/api/endpoints'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import type { Paginated, SearchHitResponse } from '@/api/types'
import { useDebounced } from '@/lib/useDebounced'

export const MIN_QUERY_LENGTH = 2

/**
 * One field searches OP number, phone and name — the server detects the shape.
 * Debounced, and the in-flight request is cancelled when the query changes: a
 * slow response for "9876" must never overwrite a fresh one for "98765".
 */
export function useSearch(rawQuery: string) {
  const query = useDebounced(rawQuery.trim())
  const enabled = query.length >= MIN_QUERY_LENGTH

  const result = useQuery<Paginated<SearchHitResponse>, ApiError>({
    queryKey: queryKeys.search(query),
    queryFn: ({ signal }) => patients.search(query, 20, null, signal),
    enabled,
    // Keeps the previous page visible while the next one loads, so the list does
    // not blank out on every keystroke.
    placeholderData: keepPreviousData,
  })

  return {
    query,
    enabled,
    hits: result.data?.data ?? [],
    isLoading: enabled && result.isLoading,
    isFetching: result.isFetching,
    error: result.error ?? null,
  }
}
